import {
  ColumnCheckboxGroupFilter,
  ColumnMultiSelectFilter,
  FilterSection,
} from 'components/data-table';
import {
  AM_PRESENCE_OPTIONS,
  HOLE_DIRECTION_OPTIONS,
  STATUS_OPTIONS,
  STREAM_OPTIONS,
  WELL_STATUS_LABELS,
} from '../utils';
import styles from './slot-filters.module.scss';

interface SlotFiltersProps {
  operators: string[];
  wellStatuses: string[];
}

/**
 * Left filter sidebar for the slot comparison table. Mounted by `DataTable`'s
 * `renderFilterPanel` slot; the `Column*Filter` wrappers read/write column
 * filter state via `useDataTableContext` internally, so this component owns
 * no state of its own.
 *
 * @param {SlotFiltersProps} props - Component props.
 * @param {string[]} props.operators - Distinct operator names for the
 *   Operator filter options.
 * @param {string[]} props.wellStatuses - Distinct normalized well-status codes
 *   for the Well Status filter options.
 * @return {JSX.Element} The rendered filter sidebar content.
 */
export const SlotFilters = ({
  operators,
  wellStatuses,
}: SlotFiltersProps): JSX.Element => (
  <div className={styles.filtersWrapper}>
    <FilterSection title="Status">
      <ColumnCheckboxGroupFilter columnId="view" options={STATUS_OPTIONS} />
    </FilterSection>
    <FilterSection title="Production Stream">
      <ColumnCheckboxGroupFilter columnId="streams" options={STREAM_OPTIONS} />
    </FilterSection>
    <FilterSection title="Hole Direction">
      <ColumnCheckboxGroupFilter
        columnId="holeDirection"
        options={HOLE_DIRECTION_OPTIONS}
      />
    </FilterSection>
    <FilterSection title="Interest Calculation Source">
      <ColumnCheckboxGroupFilter
        columnId="amPresence"
        options={AM_PRESENCE_OPTIONS}
      />
    </FilterSection>
    <FilterSection title="Operator">
      <ColumnMultiSelectFilter
        columnId="operator"
        label="Operator"
        options={operators.map((operator) => ({
          value: operator,
          label: operator,
        }))}
      />
    </FilterSection>
    <FilterSection title="Well Status">
      {/* Inline checkbox group (not a multi-select rollup): the well-status set
          is small + fixed, so surface every option directly like Status. */}
      <ColumnCheckboxGroupFilter
        columnId="wellStatus"
        options={wellStatuses.map((wellStatus) => ({
          value: wellStatus,
          label: WELL_STATUS_LABELS[wellStatus] ?? wellStatus,
        }))}
      />
    </FilterSection>
  </div>
);
